import { Col, Empty, Row, Select, Skeleton, Typography } from 'antd';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React, { useMemo } from 'react';
import { glassCardStyle } from '../../Performance/utils';

const { Title, Text } = Typography;

interface LeadsByAreaCardProps {
  data: any[];
  propertyAreas: any[];
  selectedAreaIds: string[];
  setSelectedAreaIds: (ids: string[]) => void;
  areaFilterLoading: boolean;
  leadFollowUpStatuses: any[];
  navigate: any;
  loading: boolean;
}

const LeadsByAreaChart = ({
  data,
  navigate,
  leadFollowUpStatuses,
}: {
  data: any[];
  navigate: any;
  leadFollowUpStatuses: any[];
}) => {
  const statusNameToIdMap = useMemo(() => {
    return leadFollowUpStatuses.reduce((acc, status) => {
      acc[status.name.toLowerCase()] = status.id;
      return acc;
    }, {} as { [key: string]: string });
  }, [leadFollowUpStatuses]);

  const chartOptions = useMemo(() => {
    return {
      chart: {
        type: 'bar',
        height: 400,
        backgroundColor: 'transparent',
        style: {
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial',
        },
      },
      title: {
        text: 'Leads Berdasarkan Area Properti',
        style: {
          color: '#1e293b',
          fontWeight: '700',
          fontSize: '16px',
        },
      },
      xAxis: {
        categories: data.map(item => item.property_area_name),
        title: {
          text: null,
        },
        labels: {
          style: {
            color: '#64748b',
            fontWeight: '600',
          },
        },
      },
      yAxis: {
        min: 0,
        title: {
          text: 'Jumlah Leads',
          align: 'high',
          style: { color: '#64748b' },
        },
        labels: {
          overflow: 'justify',
          style: { color: '#64748b' },
        },
        gridLineColor: 'rgba(226, 232, 240, 0.6)',
        stackLabels: {
          enabled: true,
          style: {
            fontWeight: 'bold',
            color: '#475569',
            textOutline: 'none',
          },
        },
      },
      tooltip: {
        headerFormat: '<b>{point.x}</b><br/>',
        pointFormat: '{series.name}: {point.y}<br/>Total: {point.stackTotal}',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#e2e8f0',
        shadow: true,
      },
      plotOptions: {
        bar: {
          stacking: 'normal',
          dataLabels: {
            enabled: true,
            style: {
              textOutline: 'none',
              fontWeight: '600',
            },
          },
          cursor: 'pointer',
          events: {
            click: (event: any) => {
              const point = event.point;
              const propertyAreaId = data[point.index]?.property_area_id;
              const statusName = point.series.name.toLowerCase();
              const statusId = statusNameToIdMap[statusName];

              if (propertyAreaId && statusId) {
                navigate(`/leads/data?property_area_id=${propertyAreaId}&StatusFU=${statusId}`);
              } else if (propertyAreaId) {
                navigate(`/leads/data?property_area_id=${propertyAreaId}`);
              }
            },
          },
        },
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        borderWidth: 0,
        backgroundColor: 'transparent',
        itemStyle: {
          color: '#475569',
          fontWeight: '600',
        },
      },
      credits: {
        enabled: false,
      },
      series: [
        {
          name: 'O K',
          data: data.map(item => item.ok_leads || 0),
          color: '#69d480',
        },
        {
          name: 'RSV',
          data: data.map(item => item.prospect_leads || 0),
          color: '#419f11',
        },
        {
          name: 'Hot',
          data: data.map(item => item.hot_leads || 0),
          color: '#ff4d4f',
        },
        {
          name: 'Cold',
          data: data.map(item => item.cold_leads || 0),
          color: '#1890ff',
        },

      ],
    };
  }, [data, navigate, statusNameToIdMap]);

  if (!data || data.length === 0) {
    return <Empty description="Tidak ada data leads berdasarkan area" style={{ padding: '40px 0' }} />;
  }

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} />;
};

export const LeadsByAreaCard: React.FC<LeadsByAreaCardProps> = ({
  data,
  propertyAreas,
  selectedAreaIds,
  setSelectedAreaIds,
  areaFilterLoading,
  leadFollowUpStatuses,
  navigate,
  loading,
}) => {
  return (
    <div style={glassCardStyle} className="glass-card-interactive">
      <Row justify="space-between" align="middle" style={{ marginBottom: '20px', gap: '12px' }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: '#1e293b', fontWeight: 700 }}>
            Penyebaran Leads per Kawasan Properti
          </Title>
          <Text type="secondary" style={{ fontSize: '13px' }}>
            Klasifikasi suhu prospek (Hot, Cold, RSV, O K) di setiap lokasi wilayah.
          </Text>
        </Col>
        <Col xs={24} sm="auto">
          <Select
            mode="multiple"
            allowClear
            style={{ minWidth: '220px', width: '100%' }}
            placeholder="Saring Area Properti..."
            value={selectedAreaIds}
            onChange={setSelectedAreaIds}
            loading={areaFilterLoading}
            maxTagCount="responsive"
            className="glass-select"
          >
            {propertyAreas.map(area => (
              <Select.Option key={area.id} value={area.id}>
                {area.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>

      {loading ? (
        <div style={{ padding: '40px 0' }}>
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      ) : (
        <LeadsByAreaChart
          data={data}
          navigate={navigate}
          leadFollowUpStatuses={leadFollowUpStatuses}
        />
      )}
    </div>
  );
};

export default LeadsByAreaCard;
